/**
 * Server-side history snapshots for deep replay. The in-browser ring buffer
 * covers the live session (~30 min); scrubbing beyond it (e.g. replaying a
 * recorded mission session) falls back to `GET /api/history/:layer?at=` —
 * fetched per (layer, time-bucket), cached, and rendered when it arrives.
 */

import type { Track } from '@vtak/shared';
import { CLIENT_CONFIG } from '@/config';
import { authHeaders } from '@/services/api-client';

/** Snapshot time granularity — matches the recorder's 30s sampling. */
const BUCKET_MS = 30_000;

/** Bounded cache of fetched snapshots keyed by `layer:bucket`. */
const cache = new Map<string, Track[]>();
const inFlight = new Set<string>();
const MAX_CACHED = 400;

/** A row of the history snapshot API. */
interface HistoryRow {
  uid: string;
  lat: number;
  lon: number;
  cot_type: string | null;
  props: Record<string, string | number | boolean>;
}

/** Map a history row back to a renderable Track. */
function toTrack(layer: string, row: HistoryRow, t: number): Track {
  return {
    uid: row.uid,
    type: row.cot_type ?? 'a-f-G',
    callsign: typeof row.props['name'] === 'string' ? String(row.props['name']) : row.uid,
    lat: row.lat,
    lon: row.lon,
    source: 'history',
    layerId: layer,
    domain: 'event',
    affiliation: 'friendly',
    timestamp: t,
    props: row.props,
  };
}

/** Cache key for a layer at a bucketed time. */
function keyFor(layer: string, t: number): string {
  return `${layer}:${Math.floor(t / BUCKET_MS)}`;
}

/** Drop the oldest cache entries beyond the bound. */
function pruneCache(): void {
  while (cache.size > MAX_CACHED) {
    const oldest = cache.keys().next();
    if (oldest.done) return;
    cache.delete(oldest.value);
  }
}

/**
 * The cached server snapshot for a layer at time `t`, or null when not yet
 * fetched (a fetch is kicked off; `onArrive` fires when it lands).
 */
export function serverSnapshotAt(layer: string, t: number, onArrive: () => void): Track[] | null {
  const key = keyFor(layer, t);
  const cached = cache.get(key);
  if (cached) return cached;
  if (!inFlight.has(key)) {
    inFlight.add(key);
    void fetchSnapshot(layer, t, key, onArrive);
  }
  return null;
}

/** Fetch one snapshot bucket and cache it. */
async function fetchSnapshot(layer: string, t: number, key: string, onArrive: () => void): Promise<void> {
  try {
    const at = new Date(Math.floor(t / BUCKET_MS) * BUCKET_MS).toISOString();
    const res = await fetch(
      `${CLIENT_CONFIG.apiBase}/api/history/${encodeURIComponent(layer)}?at=${encodeURIComponent(at)}`,
      authHeaders(),
    );
    const rows = res.ok ? ((await res.json()) as HistoryRow[]) : [];
    cache.set(key, rows.map((row) => toTrack(layer, row, t)));
    pruneCache();
    onArrive();
  } catch {
    cache.set(key, []);
  } finally {
    inFlight.delete(key);
  }
}
